import ReactMarkdown from 'react-markdown';
import { ExternalLink } from 'lucide-react';
import type { Article } from './ArticleCard';

interface Props {
  article: Pick<Article, 'content' | 'url'>;
  /** Site domain shown in the empty-state fallback link. */
  domain?: string;
  /**
   * For the full-page reader (text-selection highlights). Inline reader
   * doesn't use it. When provided, the wrapper attaches onMouseUp.
   */
  onMouseUp?: () => void;
}

export function ArticleBody({ article, domain, onMouseUp }: Props) {
  if (!article.content) {
    return (
      <div className="rounded-xl p-6 text-center bg-surface-0 border border-border-subtle">
        <p className="text-sm mb-4 text-text-muted">Full content not yet extracted.</p>
        <a href={article.url} target="_blank" rel="noopener noreferrer" className="text-sm font-semibold inline-flex items-center gap-1.5 text-prism-violet">
          Read on {domain || 'original site'} <ExternalLink size={14} />
        </a>
      </div>
    );
  }

  return (
    <div
      onMouseUp={onMouseUp}
      style={{
        color: '#D8D8E8',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif',
        lineHeight: 1.8,
        fontSize: '18px',
      }}
    >
      <ReactMarkdown
        components={{
          p: ({ children }) => <p style={{ marginBottom: '1.25em' }}>{children}</p>,
          a: ({ href, children }) => (
            <a href={href} target="_blank" rel="noopener noreferrer" style={{ color: '#06B6D4', textDecoration: 'underline', textDecorationColor: 'rgba(6,182,212,0.3)' }}>{children}</a>
          ),
          h1: ({ children }) => <h1 style={{ color: '#F0F0F8', fontSize: '1.6em', fontWeight: 700, marginTop: '2em', marginBottom: '0.5em', letterSpacing: '-0.02em' }}>{children}</h1>,
          h2: ({ children }) => <h2 style={{ color: '#F0F0F8', fontSize: '1.35em', fontWeight: 600, marginTop: '1.75em', marginBottom: '0.5em', letterSpacing: '-0.01em' }}>{children}</h2>,
          h3: ({ children }) => <h3 style={{ color: '#E0E0EC', fontSize: '1.15em', fontWeight: 600, marginTop: '1.5em', marginBottom: '0.4em' }}>{children}</h3>,
          strong: ({ children }) => <strong style={{ color: '#EEEEF8', fontWeight: 600 }}>{children}</strong>,
          em: ({ children }) => <em style={{ color: '#C8C8DC' }}>{children}</em>,
          blockquote: ({ children }) => (
            <blockquote style={{ borderLeft: '3px solid #8B5CF6', paddingLeft: '1.25em', margin: '1.5em 0', color: '#A0A0BC', fontStyle: 'italic' }}>{children}</blockquote>
          ),
          code: ({ children }) => (
            <code style={{ background: '#16162A', color: '#06B6D4', padding: '2px 7px', borderRadius: '5px', fontSize: '0.85em', fontFamily: 'ui-monospace, monospace' }}>{children}</code>
          ),
          pre: ({ children }) => (
            <pre style={{ background: '#16162A', padding: '1.25em', borderRadius: '10px', overflowX: 'auto', fontSize: '14px', fontFamily: 'ui-monospace, monospace', margin: '1.5em 0', border: '1px solid #1E1E34' }}>{children}</pre>
          ),
          ul: ({ children }) => <ul style={{ paddingLeft: '1.5em', marginBottom: '1em' }}>{children}</ul>,
          ol: ({ children }) => <ol style={{ paddingLeft: '1.5em', marginBottom: '1em' }}>{children}</ol>,
          li: ({ children }) => <li style={{ marginBottom: '0.4em' }}>{children}</li>,
          hr: () => <hr style={{ border: 'none', borderTop: '1px solid #1E1E34', margin: '2em 0' }} />,
          img: ({ src, alt }) => (
            <img src={src} alt={alt} style={{ maxWidth: '100%', borderRadius: '8px', margin: '1.5em 0' }} />
          ),
        }}
      >
        {article.content}
      </ReactMarkdown>
    </div>
  );
}
